/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Ref, useCallback, useEffect, useRef } from 'preact/hooks';
import { useToggle } from '../hooks/UNSAFE_useToggle';
import { StepButton } from './StepButton';
import { Steppers } from './Steppers';

const SPIN_DELAY = 500;
const SPIN_INTERVAL = 40;

export type StepDirection = 'increase' | 'decrease';

export type StepEvent = {
  /**
   * The direction of the requested step.
   */
  direction: StepDirection;
};

type Props = {
  /**
   * Controls whether the step buttons are shown.
   */
  hasSteppers?: boolean;

  /**
   * A ref to the input element.
   */
  inputRef: Ref<HTMLInputElement>;

  /**
   * Controls whether the step down button is disabled.
   */
  isStepDownDisabled?: boolean;

  /**
   * Controls whether the step up button is disabled.
   */
  isStepUpDisabled?: boolean;

  /**
   * Called when a continuous step is requested (aka "spin")
   * by holding down the arrow up or arrow down keys, or
   * by pressing and holding down a step button.
   */
  onSpin?: (event: StepEvent) => void;

  /**
   * Called when spinning is complete and the user has
   * released the arrow key or step button.
   */
  onSpinComplete?: () => void;

  /**
   * Called when a single step is requested by clicking a step button
   * or by pressing the arrow up or arrow down keys.
   */
  onStep?: (event: StepEvent) => void;
};

/**
 * A hook for using stepping functionality with NumberInputText. Stepping can
 * be accomplished using the step up or down buttons or by pressing the arrow
 * up or arrow down keys. Holding down the buttons or keys produces continuous
 * step events.
 *
 * @param hasSteppers Controls whether the step buttons are shown
 * @param inputRef A ref to the input element
 * @param isStepDownDisabled Controls whether stepping down is disabled
 * @param isStepUpDisabled Controls whether stepping up is disabled
 * @param onSpin Called when a continuous step (aka spin) is requested
 * @param onSpinComplete Called when spinning is complete
 * @param onStep Called when a single step is requested
 * @returns A JSX element which can be placed in the endContent of NumberInputText.
 */
export const useSteppers = ({
  hasSteppers,
  inputRef,
  isStepDownDisabled,
  isStepUpDisabled,
  onSpin,
  onSpinComplete,
  onStep
}: Props) => {
  const { bool: isStarted, setTrue: setStartedTrue, setFalse: setStartedFalse } = useToggle(false);
  const {
    bool: isSpinning,
    setTrue: setSpinningTrue,
    setFalse: setSpinningFalse
  } = useToggle(false);
  const startTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const spinTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const directionRef = useRef<StepDirection>('increase');
  const spinCompleteRef = useRef(false);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!(event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        return;
      }
      // By default, pressing the arrow keys moves the cursor to the
      // beginning or end which we don't want, so call preventDefault.
      // We also want to stop propagation of handled events.
      event.preventDefault();
      event.stopPropagation();

      if (isStarted) {
        // If already started, ignore repeated key down events.
        return;
      }
      if (
        (event.key === 'ArrowDown' && isStepDownDisabled) ||
        (event.key === 'ArrowUp' && isStepUpDisabled)
      ) {
        // If requested step is disabled, return.
        return;
      }
      directionRef.current = event.key === 'ArrowDown' ? 'decrease' : 'increase';
      setStartedTrue();
      // Do an initial step, which is what oj-input-number did as well.
      onStep?.({ direction: directionRef.current });
    },
    [isStarted, onStep, isStepDownDisabled, isStepUpDisabled, setStartedTrue]
  );

  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (isSpinning) {
          // Only want to call onSpinComplete if we were actually spinning.
          spinCompleteRef.current = true;
        }
        setStartedFalse();
        setSpinningFalse();
        event.stopPropagation();
      }
    },
    [isSpinning, setStartedFalse, setSpinningFalse]
  );

  useEffect(() => {
    if (isStarted && !startTimerRef.current) {
      startTimerRef.current = setTimeout(() => {
        setSpinningTrue();
      }, SPIN_DELAY);
    }
    return () => {
      // Clean up between renders.
      if (startTimerRef.current) {
        clearTimeout(startTimerRef.current);
        startTimerRef.current = null;
      }
    };
  }, [isStarted, setSpinningTrue]);

  useEffect(() => {
    if (!isSpinning) {
      if (spinTimerRef.current) {
        clearInterval(spinTimerRef.current);
        spinTimerRef.current = null;
      }
    } else {
      if (!spinTimerRef.current) {
        spinTimerRef.current = setInterval(() => {
          // If further spinning is disabled, call setSpinningFalse to
          // clean up the timer otherwise repeat the step.
          if (
            (directionRef.current === 'increase' && isStepUpDisabled) ||
            (directionRef.current === 'decrease' && isStepDownDisabled)
          ) {
            setSpinningFalse();
            spinCompleteRef.current = true;
          } else {
            onSpin?.({ direction: directionRef.current });
          }
        }, SPIN_INTERVAL);
      }
    }
    return () => {
      // Clean up between renders.
      if (spinTimerRef.current) {
        clearInterval(spinTimerRef.current);
        spinTimerRef.current = null;
      }
    };
  }, [isSpinning, isStepDownDisabled, isStepUpDisabled, onSpin, setSpinningFalse]);

  // Only want to run this effect when isSpinning is false and our ref is true.
  // This helps guarantee onSpinComplete is only called once after spinning stops,
  // even if onSpinComplete's identity is changing.
  useEffect(() => {
    if (!isSpinning && spinCompleteRef.current) {
      spinCompleteRef.current = false;
      onSpinComplete?.();
    }
  }, [isSpinning, onSpinComplete]);

  const handleClick = useCallback(
    (event: StepEvent) => {
      // When a step button is clicked, set focus to the input.
      inputRef.current?.focus();
      onStep?.(event);
    },
    [onStep, inputRef]
  );

  const handleDisabledMouseDown = useCallback((event: Event) => {
    // Using preventDefault prevents focus from moving to the body when a disabled stepper is clicked.
    // FIXME: can we handle this use case more generally at the startContent/endContent level in TextFieldContent?
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const steppers = hasSteppers ? (
    <Steppers>
      <div onMouseDown={(e) => isStepDownDisabled && handleDisabledMouseDown(e)} aria-hidden="true">
        <StepButton
          direction="decrease"
          isDisabled={isStepDownDisabled}
          onStep={handleClick}
          variant="arrowdown"
        />
      </div>

      <div onMouseDown={(e) => isStepUpDisabled && handleDisabledMouseDown(e)} aria-hidden="true">
        <StepButton
          direction="increase"
          isDisabled={isStepUpDisabled}
          onStep={handleClick}
          variant="arrowup"
        />
      </div>
    </Steppers>
  ) : undefined;

  // stepHandlerProps will be spread onto the parent to handle
  // stepping functionality via the arrow up or down keys.
  const stepHandlerProps = hasSteppers ? { onKeyDown, onKeyUp } : {};

  return { steppers, stepHandlerProps };
};
